import { z } from 'zod';
import type { FormName } from '@/lib/analytics';

/**
 * Form names allowed to post to `/api/contact`. An explicit allow-list rather
 * than the whole `FormName` union: the brief, newsletter and CRO-audit forms
 * have their own endpoints, so accepting their names here would let a crafted
 * request book a lead value this endpoint never produces.
 */
export const CONTACT_FORM_NAMES = [
  'contact_form',
  'case_study_quote',
  'clutch_consultation',
  'collaboration',
  'cta_band',
  'service_consultation',
  'services_catalog_mini',
  'chat_lead',
  'ai_readiness_report',
  'accessibility_report',
] as const satisfies readonly FormName[];

/** Newsletter subscription form schema */
/**
 * Hidden anti-bot field. Humans never see it, so anything in it is a bot.
 *
 * It is deliberately NOT `.max(0)`. Every lead route already handles a filled
 * honeypot itself — log `<route>.honeypot_triggered`, return 200 with a
 * success envelope, create no lead — which is the standard pattern: tell the
 * bot nothing. `.max(0)` made `safeParse` fail first, so all four routes
 * answered 400 instead, every one of those branches was unreachable, and the
 * `honeypot_triggered` events could never fire. A bot-detection metric that
 * reads zero because it cannot be written is worse than no metric
 * (finding conversion-ipr5bo).
 *
 * Bounded rather than unbounded so a filled honeypot cannot be used to push a
 * large payload through validation.
 */
const HONEYPOT_FIELD = z.string().max(200).optional();

export const newsletterSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  consent: z.literal(true, {
    errorMap: () => ({ message: 'You must agree to the privacy policy' }),
  }),
  honeypot: HONEYPOT_FIELD,
});

export type NewsletterFormData = z.infer<typeof newsletterSchema>;

/**
 * Contact form schema — short-form CTA on About / Home contact sections.
 * Message is optional because the design "Get in Touch" card only asks for
 * name + email + consent. The detailed brief form has its own schema.
 */
export const contactFormSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(100),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().max(20).optional(),
  company: z.string().max(100).optional(),
  website: z.string().max(200).optional(),
  message: z.string().max(2000).optional(),
  consent: z.literal(true, {
    errorMap: () => ({ message: 'You must agree to the privacy policy' }),
  }),
  honeypot: HONEYPOT_FIELD,
  /**
   * Which surface produced the lead. Labels the notification email and, more
   * importantly, attributes the server-side `generate_lead` backup and prices
   * it via `scoreLead()` — without it every form reads as a `contact_form`
   * worth 50. The validation, storage and delivery path are identical for all
   * of them, which is the point: surfaces reuse this endpoint rather than
   * growing their own. Optional, defaulting to the plain contact form.
   */
  formName: z.enum(CONTACT_FORM_NAMES).optional(),
});

export type ContactFormData = z.infer<typeof contactFormSchema>;

/**
 * Detailed brief form — long-form project brief on the About page.
 * Captures services, budget tier, free-text brief, full contact info.
 */
export const detailedBriefSchema = z.object({
  firstName: z.string().min(1, 'First name is required').max(50),
  lastName: z.string().min(1, 'Last name is required').max(50),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().min(3, 'Phone is required').max(30),
  services: z.array(z.string()).optional().default([]),
  budget: z.string().optional(),
  brief: z.string().max(5000).optional(),
  consent: z.literal(true, {
    errorMap: () => ({ message: 'You must agree to the privacy policy' }),
  }),
  honeypot: HONEYPOT_FIELD,
});

export type DetailedBriefFormData = z.infer<typeof detailedBriefSchema>;

/**
 * Free CRO Audit form — bottom-of-funnel offer on the /clutch page. Lean field
 * set: the `website` is required because it's the store EVDEV audits.
 */
export const croAuditSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(100),
  email: z.string().email('Please enter a valid email address'),
  website: z
    .string()
    .min(3, 'Please enter your store URL')
    .max(200, 'URL is too long'),
  message: z.string().max(2000).optional(),
  consent: z.literal(true, {
    errorMap: () => ({ message: 'You must agree to the privacy policy' }),
  }),
  honeypot: HONEYPOT_FIELD,
});

export type CroAuditFormData = z.infer<typeof croAuditSchema>;
